import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { fmtDateTime, fmtDuration } from '../utils/date';
import type { PatrolSession, PatrolType } from '../types';

interface Props {
  patrols: PatrolSession[];
  onViewReplay?: (patrol: PatrolSession) => void;
  loading?: boolean;
  showGuardName?: boolean;
  patrolTypes?: PatrolType[];
  onSetType?: (patrolId: string, typeId: string | null) => void;
}

function CountBadge({ count, to }: { count: number; to: string }) {
  if (count === 0) {
    return <span className="text-gray-400">0</span>;
  }
  return (
    <Link to={to} className="text-brand-600 hover:underline font-medium">
      {count}
    </Link>
  );
}

export default function PatrolTable({
  patrols,
  onViewReplay,
  loading,
  showGuardName = true,
  patrolTypes,
  onSetType,
}: Props) {
  const role = useAuthStore((s) => s.role()) ?? 'admin';
  const [editingTypeId, setEditingTypeId] = useState<string | null>(null);

  if (loading) {
    return <div className="text-center py-12 text-gray-400">Loading patrols…</div>;
  }

  // Only show completed patrols (ended_at set)
  const completed = patrols.filter((p) => p.ended_at != null);

  if (completed.length === 0) {
    return <div className="text-center py-12 text-gray-400">No patrols found.</div>;
  }

  const canEditType = !!(patrolTypes && onSetType);

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left border-b border-gray-200">
            <th className="pb-2 pr-4 font-semibold text-gray-600">Started</th>
            {showGuardName && <th className="pb-2 pr-4 font-semibold text-gray-600">Patroller</th>}
            <th className="pb-2 pr-4 font-semibold text-gray-600">Duration</th>
            <th className="pb-2 pr-4 font-semibold text-gray-600">Patrol Type</th>
            <th className="pb-2 pr-4 font-semibold text-gray-600">Alarms</th>
            <th className="pb-2 pr-4 font-semibold text-gray-600">Incidents</th>
            <th className="pb-2 pr-4 font-semibold text-gray-600">Patrol Report</th>
            {onViewReplay && <th className="pb-2 font-semibold text-gray-600" />}
          </tr>
        </thead>
        <tbody>
          {completed.map((p) => (
            <tr key={p.id} className="border-b border-gray-100 hover:bg-gray-50">
              <td className="py-3 pr-4 text-gray-700">
                {fmtDateTime(p.started_at)}
              </td>
              {showGuardName && (
                <td className="py-3 pr-4">
                  <span className="font-medium text-gray-900">{p.guard_name}</span>
                </td>
              )}
              <td className="py-3 pr-4 text-gray-500">
                {fmtDuration(p.started_at, p.ended_at)}
              </td>
              {/* Patrol Type */}
              <td className="py-3 pr-4">
                {editingTypeId === p.id && canEditType ? (
                  <select
                    autoFocus
                    value={p.patrol_type ?? ''}
                    onChange={(e) => {
                      onSetType!(p.id, e.target.value || null);
                      setEditingTypeId(null);
                    }}
                    onBlur={() => setEditingTypeId(null)}
                    className="border border-gray-300 rounded px-2 py-1 text-xs text-gray-700 focus:outline-none focus:ring-1 focus:ring-brand-500 bg-white"
                  >
                    <option value="">— clear —</option>
                    {patrolTypes!.map((t) => (
                      <option key={t.id} value={t.name}>
                        {t.name}
                      </option>
                    ))}
                  </select>
                ) : (
                  <span
                    onClick={() => canEditType && setEditingTypeId(p.id)}
                    title={canEditType ? 'Click to change type' : undefined}
                    className={`text-xs text-gray-700 ${canEditType ? 'cursor-pointer underline decoration-dotted hover:text-gray-900' : ''}`}
                  >
                    {p.patrol_type ?? <span className="text-gray-400">—</span>}
                  </span>
                )}
              </td>
              {/* Alarms */}
              <td className="py-3 pr-4 tabular-nums">
                <CountBadge count={p.alarm_count ?? 0} to={`/${role}/events`} />
              </td>
              {/* Incidents */}
              <td className="py-3 pr-4 tabular-nums">
                <CountBadge count={p.incident_count ?? 0} to={`/${role}/events`} />
              </td>
              {/* Patrol Report — 1 if end-of-patrol report filed, else 0 */}
              <td className="py-3 pr-4 tabular-nums">
                <CountBadge count={p.outcome === 'incident' ? 1 : 0} to={`/${role}/events`} />
              </td>
              {onViewReplay && (
                <td className="py-3">
                  {Number(p.point_count) >= 2 ? (
                    <button
                      onClick={() => onViewReplay(p)}
                      className="px-3 py-1 text-xs rounded bg-brand-100 text-brand-700 hover:bg-brand-200 transition-colors"
                    >
                      Route
                    </button>
                  ) : null}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
